import { WitzyLogo, WitzyMark } from '@components/branding/WitzyLogo';
import { useInboxUnreadCount } from '@hooks/useInboxUnreadCount';
import { cn } from '@lib/utils';
import { useUiStore } from '@stores/ui.store';
import { Tooltip } from '@ui/index';
import {
  BarChart3,
  ChevronLeft,
  CreditCard,
  FolderKanban,
  LayoutDashboard,
  Megaphone,
  MessageCircle,
  Settings,
  Users2,
  type LucideIcon,
} from 'lucide-react';
import { FileText } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';



// --- Navigation config -------------------------------------------------------

interface NavItem {
  icon: LucideIcon;
  label: string;
  href: string;
  accentGradient: string;
  accentGlow: string;
  badgeKey?: string;
}

const NAV_ITEMS: NavItem[] = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/', accentGradient: 'from-emerald-400 to-teal-500', accentGlow: 'rgba(52,211,153,0.30)' },
  { icon: FolderKanban, label: 'Projects', href: '/projects', accentGradient: 'from-slate-400 to-gray-500', accentGlow: 'rgba(148,163,184,0.30)' },
  { icon: MessageCircle, label: 'Inbox', href: '/inbox', accentGradient: 'from-blue-400 to-indigo-500', accentGlow: 'rgba(96,165,250,0.30)' },
  { icon: Users2, label: 'Contacts', href: '/contacts', accentGradient: 'from-violet-400 to-purple-500', accentGlow: 'rgba(167,139,250,0.30)' },
  { icon: Megaphone, label: 'Campaigns', href: '/campaigns', accentGradient: 'from-amber-400 to-orange-500', accentGlow: 'rgba(251,191,36,0.30)' },
  { icon: FileText, label: 'Templates', href: '/templates', accentGradient: 'from-pink-400 to-rose-500', accentGlow: 'rgba(251,113,133,0.30)' },
  // Automation is intentionally hidden — see docs/deferred-features.md.
  // The route still exists, but it's not exposed in the nav until the
  // executor + UX gaps flagged in the May 2026 audit are fixed.
  // { icon: Workflow, label: 'Automation', href: '/automation', accentGradient: 'from-cyan-400 to-blue-500', accentGlow: 'rgba(34,211,238,0.30)' },
  // Catalog hidden for now — it's local-only CRUD that implies a Meta Commerce
  // sync / product-sharing flow that isn't built. Re-enable once the Aisensy
  // catalog integration ships.
  // { icon: ShoppingBag, label: 'Catalog', href: '/catalog', accentGradient: 'from-teal-400 to-emerald-500', accentGlow: 'rgba(45,212,191,0.30)' },
  { icon: BarChart3, label: 'Analytics', href: '/analytics', accentGradient: 'from-indigo-400 to-purple-500', accentGlow: 'rgba(129,140,248,0.30)' },
];

const BOTTOM_ITEMS: NavItem[] = [
  { icon: CreditCard, label: 'Billing', href: '/billing', accentGradient: 'from-green-400 to-teal-500', accentGlow: 'rgba(52,211,153,0.30)' },
  { icon: Settings, label: 'Settings', href: '/settings', accentGradient: 'from-gray-400 to-gray-500', accentGlow: 'rgba(142,142,147,0.30)' },
];

// --- Component ---------------------------------------------------------------

export function Sidebar(): JSX.Element {
  const location = useLocation();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const inboxUnread = useInboxUnreadCount();

  const isActive = (href: string): boolean =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const renderItem = (item: NavItem): JSX.Element => {
    const active = isActive(item.href);
    const Icon = item.icon;
    // Per-item badge count. Only Inbox has one today; the lookup is
    // extensible — add more keys to the switch as new badges land.
    const badgeCount = item.href === '/inbox' ? inboxUnread : 0;
    const showBadge = badgeCount > 0;
    const badgeLabel = badgeCount > 99 ? '99+' : String(badgeCount);

    const iconEl = (
      <span
        className={cn(
          'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200',
          active
            ? `bg-gradient-to-br ${item.accentGradient} text-white`
            : 'bg-apple-gray-100 text-apple-gray-600 group-hover:bg-apple-gray-200 group-hover:text-apple-label-secondary',
        )}
        style={active ? { boxShadow: `0 4px 12px ${item.accentGlow}` } : undefined}
      >
        <Icon size={16} strokeWidth={1.5} />
        {showBadge && collapsed ? (
          // Collapsed sidebar — overlay a small dot indicator on the
          // icon since there's no room for the numeric pill.
          <span
            aria-label={`${badgeCount} unread`}
            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500"
          />
        ) : null}
      </span>
    );

    const row = (
      <Link
        key={item.href}
        to={item.href}
        className={cn(
          'group relative flex items-center gap-3 rounded-xl px-3 py-2 transition-all duration-200 ease-apple',
          active
            ? 'bg-gradient-to-r from-[#F0FDF4] to-[#F0FDFA] border-l-2 border-brand-600'
            : 'hover:bg-apple-gray-100',
          collapsed && 'justify-center px-0',
        )}
      >
        {iconEl}
        {!collapsed ? (
          <span
            className={cn(
              'flex flex-1 items-center justify-between gap-2 text-[14px] transition-colors duration-150',
              active
                ? 'font-semibold text-apple-label'
                : 'font-medium text-apple-gray-700 group-hover:text-apple-label',
            )}
          >
            <span>{item.label}</span>
            {showBadge ? (
              <span
                aria-label={`${badgeCount} unread`}
                className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white"
              >
                {badgeLabel}
              </span>
            ) : null}
          </span>
        ) : null}
      </Link>
    );

    return collapsed ? (
      <Tooltip key={item.href} content={item.label} side="right">
        {row}
      </Tooltip>
    ) : (
      row
    );
  };


  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-apple-divider bg-white transition-[width] duration-250 ease-apple',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      {/* Header */}
      <div className={cn('flex h-16 shrink-0 items-center border-b border-apple-divider', collapsed ? 'justify-center px-2' : 'justify-between px-4')}>
        {!collapsed ? (
          // Full lock-up — mark + wordmark in a single image
          <Link to="/" className="flex items-center">
            <WitzyLogo textSize={18} />
          </Link>
        ) : (
          // Collapsed sidebar — just the glyph
          <Link to="/" className="flex h-8 w-8 items-center justify-center">
            <WitzyMark size={28} />
          </Link>
        )}
        <button
          type="button"
          onClick={toggle}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md text-apple-gray-500 transition hover:bg-apple-gray-100 hover:text-apple-label',
            collapsed && 'rotate-180',
          )}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronLeft size={16} strokeWidth={1.5} />
        </button>
      </div>

      {/* Main nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <div className="flex flex-col gap-0.5">
          {NAV_ITEMS.map(renderItem)}
        </div>
      </nav>

      {/* Divider */}
      <div className="mx-4 border-t border-apple-divider" />

      {/* Bottom nav */}
      <div className="px-2 py-3">
        <div className="flex flex-col gap-0.5">
          {BOTTOM_ITEMS.map(renderItem)}
        </div>
      </div>

      {/* Divider */}
      {/* User identity moved to the TopBar dropdown (top right). The
          old sidebar button only navigated to /settings and was
          confusing — the user menu lives in one canonical place now. */}
    </aside>
  );
}
